import { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Reply, ThumbsUp } from 'lucide-react';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';

const Comment = ({ comment, postId }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyName, setReplyName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasLiked, setHasLiked] = useState(() => {
    const likedComments = JSON.parse(localStorage.getItem('likedComments') || '[]');
    return likedComments.includes(comment.id);
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleLike = async () => {
    if (hasLiked) {
      toast.error('You already liked this comment');
      return;
    }

    try {
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) return;
      
      const postData = postDoc.data();
      const comments = postData.comments || [];
      
      // Find and update the specific comment
      const updatedComments = comments.map((c) => {
        if (c.id === comment.id) {
          return { ...c, likes: (c.likes || 0) + 1 };
        }
        return c;
      });
      
      // Update the entire comments array
      await updateDoc(postRef, {
        comments: updatedComments,
      });
      
      // Save to localStorage to prevent multiple likes
      const likedComments = JSON.parse(localStorage.getItem('likedComments') || '[]');
      likedComments.push(comment.id);
      localStorage.setItem('likedComments', JSON.stringify(likedComments));
      
      setHasLiked(true);
      toast.success('Liked!');
    } catch (error) {
      console.error('Error liking comment:', error);
      toast.error('Failed to like comment');
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !replyName.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const newReply = {
        id: `reply_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: replyName.trim(),
        text: replyText.trim(),
        createdAt: new Date(),
        likes: 0,
        parentId: comment.id,
      };

      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        comments: arrayUnion(newReply),
      });

      toast.success('Reply posted!');
      setReplyText('');
      setReplyName('');
      setShowReplyForm(false);
      // Real-time listener will automatically update comments
    } catch (error) {
      console.error('Error posting reply:', error);
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`${comment.parentId ? 'ml-12 mt-4' : 'mt-6'}`}
    >
      <div className="bg-gray-50 rounded-lg p-4">
        {/* Comment Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center font-semibold">
              {comment.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <h4 className="font-body font-semibold text-sm">{comment.name || 'Anonymous'}</h4>
              <p className="text-xs text-gray-500">{formatDate(comment.createdAt)}</p>
            </div>
          </div>
        </div>

        {/* Comment Text */}
        <p className="text-gray-700 font-body text-sm mb-3 ml-13">{comment.text}</p>

        {/* Actions */}
        <div className="flex items-center space-x-4 ml-13">
          <button
            onClick={handleLike}
            disabled={hasLiked}
            className={`flex items-center space-x-1 text-xs font-body transition-colors ${
              hasLiked ? 'text-gray-400' : 'text-gray-600 hover:text-black'
            }`}
          >
            <ThumbsUp size={14} className={hasLiked ? 'fill-current' : ''} />
            <span>{comment.likes > 0 ? comment.likes : 'Like'}</span>
          </button>
          
          {!comment.parentId && (
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="flex items-center space-x-1 text-xs font-body text-gray-600 hover:text-black transition-colors"
            >
              <Reply size={14} />
              <span>Reply</span>
            </button>
          )}
        </div>

        {/* Reply Form */}
        {showReplyForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.3 }}
            onSubmit={handleReplySubmit}
            className="mt-4 ml-13 space-y-3"
          >
            <input
              type="text"
              value={replyName}
              onChange={(e) => setReplyName(e.target.value)}
              placeholder="Your name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your reply..."
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black resize-none"
              required
            />
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-black text-white rounded-lg text-xs font-body font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Posting...' : 'Post Reply'}
              </button>
              <button
                type="button"
                onClick={() => setShowReplyForm(false)}
                className="px-4 py-2 bg-gray-200 text-black rounded-lg text-xs font-body font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.form>
        )}
      </div>
    </motion.div>
  );
};

export default Comment;
